// =====================================================================
// FORCE CLEAR PANEL - ADMIN EMERGENCY OPERATIONS
// =====================================================================
// Purpose: Password-protected force clear operations for admins
// Password: "clearforce"
// Operations: Clear staging tables, clear print queue, clear cache
// =====================================================================

import React, { useState, useEffect } from 'react'
import './ForceClearPanel.css'

const FORCE_CLEAR_PASSWORD = 'clearforce'

export const ForceClearPanel: React.FC = () => {
  const [status, setStatus] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false)
  const [passwordInput, setPasswordInput] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [selectedOperation, setSelectedOperation] = useState<string | null>(null)
  const [operationDetails, setOperationDetails] = useState<any>(null)

  const token = localStorage.getItem('egl_token')

  // Fetch status on mount
  useEffect(() => {
    fetchStatus()
  }, [])

  const fetchStatus = async () => {
    try {
      const response = await fetch('/api/admin/force-clear/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await response.json()
      if (data.success) {
        setStatus(data.data)
      }
    } catch (error) {
      console.error('Error fetching status:', error)
    }
  }

  const handleOperationRequest = (operation: string, details: any = null) => {
    setSelectedOperation(operation)
    setOperationDetails(details)
    setShowPasswordPrompt(true)
    setPasswordError('')
    setPasswordInput('')
  }

  const handlePasswordSubmit = () => {
    if (passwordInput !== FORCE_CLEAR_PASSWORD) {
      setPasswordError('❌ Incorrect password')
      setPasswordInput('')
      return
    }

    // Password correct, execute operation
    setShowPasswordPrompt(false)
    setPasswordInput('')
    setPasswordError('')

    if (selectedOperation) {
      executeOperation(selectedOperation, operationDetails)
    }
  }

  const executeOperation = async (operation: string, details: any) => {
    setLoading(true)
    try {
      let endpoint = ''
      let body: any = { password: FORCE_CLEAR_PASSWORD }

      switch (operation) {
        case 'clear-staging-dubai':
          endpoint = '/api/admin/force-clear/staging'
          body.location = 'DUBAI'
          break
        case 'clear-staging-doha':
          endpoint = '/api/admin/force-clear/staging'
          body.location = 'DOHA'
          break
        case 'clear-queue-all':
          endpoint = '/api/admin/force-clear/print-queue'
          break
        case 'clear-queue-failed':
          endpoint = '/api/admin/force-clear/print-queue'
          body.status_filter = 'FAILED'
          break
        case 'clear-queue-pending':
          endpoint = '/api/admin/force-clear/print-queue'
          body.status_filter = 'PENDING'
          break
        case 'clear-cache':
          endpoint = '/api/admin/force-clear/cache'
          break
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (data.success) {
        alert(`✅ ${data.message}\n\n${JSON.stringify(data.data, null, 2)}`)
        fetchStatus() // Refresh status
      } else {
        alert(`❌ Operation failed: ${data.message}`)
      }
    } catch (error) {
      console.error('Error executing operation:', error)
      alert('❌ Network error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="force-clear-panel">
      <div className="panel-header">
        <h2>🚨 Force Clear Operations</h2>
        <p className="panel-warning">
          ⚠️ DANGEROUS: These operations cannot be undone. Use only when necessary.
        </p>
      </div>

      {/* Password Prompt */}
      {showPasswordPrompt && (
        <div className="force-clear-password-overlay">
          <div className="force-clear-password-modal">
            <h3>🔐 Enter Force Clear Password</h3>
            <p className="operation-name">Operation: <strong>{selectedOperation}</strong></p>
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              placeholder="Password"
              autoFocus
            />
            {passwordError && <div className="password-error">{passwordError}</div>}
            <div className="password-buttons">
              <button onClick={() => setShowPasswordPrompt(false)}>Cancel</button>
              <button onClick={handlePasswordSubmit} className="submit-btn">Execute</button>
            </div>
          </div>
        </div>
      )}

      {/* Status Display */}
      {status && (
        <div className="status-grid">
          <div className="status-card">
            <h3>📊 Staging Tables Status</h3>
            <div className="status-row">
              <span>Dubai Staging:</span>
              <strong>{status.staging_tables?.dubai || 0} certificates</strong>
            </div>
            <div className="status-row">
              <span>Doha Staging:</span>
              <strong>{status.staging_tables?.doha || 0} certificates</strong>
            </div>
            <div className="status-row total">
              <span>Total in Staging:</span>
              <strong>{status.staging_tables?.total || 0}</strong>
            </div>
          </div>

          <div className="status-card">
            <h3>🖨️ Print Queue Status</h3>
            {Object.entries(status.print_queue || {}).map(([stat, count]) => (
              <div key={stat} className="status-row">
                <span>{stat}:</span>
                <strong>{count as number}</strong>
              </div>
            ))}
            <div className="status-row total">
              <span>Total in Queue:</span>
              <strong>{status.total_queue || 0}</strong>
            </div>
          </div>
        </div>
      )}

      {/* Operations */}
      <div className="operations-grid">
        {/* Staging Operations */}
        <div className="operation-card">
          <h3>🗑️ Clear Staging Tables</h3>
          <p>Remove all certificates from ODBC staging (prevents printing)</p>
          <div className="operation-buttons">
            <button
              onClick={() => handleOperationRequest('clear-staging-dubai')}
              disabled={loading || !status?.staging_tables?.dubai}
              className="btn-danger"
            >
              Clear Dubai Staging ({status?.staging_tables?.dubai || 0})
            </button>
            <button
              onClick={() => handleOperationRequest('clear-staging-doha')}
              disabled={loading || !status?.staging_tables?.doha}
              className="btn-danger"
            >
              Clear Doha Staging ({status?.staging_tables?.doha || 0})
            </button>
          </div>
        </div>

        {/* Queue Operations */}
        <div className="operation-card">
          <h3>🗑️ Clear Print Queue</h3>
          <p>Remove print jobs from database (does not affect printed cards)</p>
          <div className="operation-buttons">
            <button
              onClick={() => handleOperationRequest('clear-queue-failed')}
              disabled={loading || !status?.print_queue?.FAILED}
              className="btn-warning"
            >
              Clear Failed Jobs ({status?.print_queue?.FAILED || 0})
            </button>
            <button
              onClick={() => handleOperationRequest('clear-queue-pending')}
              disabled={loading || !status?.print_queue?.PENDING}
              className="btn-warning"
            >
              Clear Pending Jobs ({status?.print_queue?.PENDING || 0})
            </button>
            <button
              onClick={() => handleOperationRequest('clear-queue-all')}
              disabled={loading || !status?.total_queue}
              className="btn-danger-high"
            >
              Clear All Queue ({status?.total_queue || 0})
            </button>
          </div>
        </div>

        {/* Cache Operations */}
        <div className="operation-card">
          <h3>🗑️ Clear Print Cache</h3>
          <p>Remove temporary image files (WebP, BMP, ZMJ)</p>
          <div className="operation-buttons">
            <button
              onClick={() => handleOperationRequest('clear-cache')}
              disabled={loading}
              className="btn-warning"
            >
              Clear Cache Files
            </button>
          </div>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="panel-footer">
        <button onClick={fetchStatus} disabled={loading} className="btn-refresh">
          🔄 Refresh Status
        </button>
      </div>
    </div>
  )
}

